import {
  REWARD_CABLES,
  REWARD_QUALITY_PROBABILITIES,
  RUN_BONUS_EFFECTS,
  WEEK1_GUARANTEED_CABLES,
  type RewardQuality,
} from '../config/balance.js';
import type { MapId } from '../config/maps.js';
import type { Rng } from './rng.js';
import type { RewardBonusEffect, RewardOffer, RunModifiers } from './types.js';

function cloneMods(m: RunModifiers): RunModifiers {
  return { ...m };
}

function pct(v: number): string {
  return `${Math.round(v * 100)} %`;
}

function copyFor(kind: string, quality: RewardQuality): { label: string; description: string } {
  const small = RUN_BONUS_EFFECTS.small;
  const std = RUN_BONUS_EFFECTS.standard;
  const large = RUN_BONUS_EFFECTS.large;
  switch (kind) {
    case 'pulseSpeed': {
      const amount = quality === 'small' ? small.pulseSpeed : std.pulseSpeed;
      const prod =
        quality === 'small' ? small.generatorProduction : std.generatorProduction;
      return {
        label: 'Impulsions plus rapides',
        description: `Impulsions +${pct(amount)} plus vite, et générateurs +${pct(prod)} de production (intervalle réduit).`,
      };
    }
    case 'bulbCapacity':
      return {
        label: 'Ampoules renforcées',
        description: `Capacité des ampoules +${pct(small.bulbCapacity)} : elles tiennent plus longtemps.`,
      };
    case 'extraRelays': {
      const n = quality === 'small' ? small.extraRelays : std.extraRelays;
      return {
        label: n === 1 ? 'Relais bonus' : `${n} relais bonus`,
        description: `Ajoute ${n} relais à placer pour croiser les câbles sans ralentissement.`,
      };
    }
    case 'extraSwitches': {
      const n = quality === 'small' ? small.extraSwitches : std.extraSwitches;
      return {
        label: n === 1 ? 'Switch bonus' : `${n} switches bonus`,
        description: `Ajoute ${n} switch${n > 1 ? 'es' : ''} : les réseaux qui se croisent ne se mélangent pas.`,
      };
    }
    case 'switch':
      return {
        label: 'Switch',
        description:
          'Place un switch sur un croisement : les impulsions restent sur leur réseau sans se mélanger.',
      };
    case 'edgeThroughput':
      return {
        label: 'Débit des lignes',
        description: `Chaque câble peut transporter ${pct(small.edgeThroughput)} d'impulsions en plus.`,
      };
    case 'globalThroughput': {
      const amount = quality === 'large' ? large.globalThroughput : std.globalThroughput;
      return {
        label: 'Réseau haute capacité',
        description: `Débit global du réseau +${pct(amount)} : plus d'énergie en circulation.`,
      };
    }
    case 'interferenceResist': {
      const amount = quality === 'large' ? large.interferenceResist : std.interferenceResist;
      return {
        label: 'Anti-brouillage',
        description: `Réduit de ${pct(amount)} les malus quand des câbles se croisent sans relais.`,
      };
    }
    case 'teleporter':
      return {
        label: 'Téléporteur',
        description:
          'Place un portail A et un portail B : les impulsions passent instantanément de l’un à l’autre.',
      };
    case 'expressLine':
      return {
        label: 'Ligne express',
        description: 'Toutes les impulsions gagnent un bonus de vitesse permanent.',
      };
    case 'digitalBridge':
      return {
        label: 'Pont numérique',
        description: 'Le réseau gagne en efficacité : moins de pertes sur les longs trajets.',
      };
    default:
      return { label: kind, description: 'Bonus de course pour cette partie.' };
  }
}

function pickStandardKind(rng: Rng, index: number, mapId: MapId): string {
  const withTp = [
    'pulseSpeed',
    'globalThroughput',
    'extraRelays',
    'extraSwitches',
    'interferenceResist',
    'teleporter',
  ] as const;
  const withoutTp = [
    'pulseSpeed',
    'globalThroughput',
    'extraRelays',
    'extraSwitches',
    'interferenceResist',
  ] as const;

  // Carte 1 (core): téléporteur rare parmi les Standard.
  if (mapId === 'core') {
    if (rng.next() < 0.14) return 'teleporter';
    return withoutTp[index % withoutTp.length]!;
  }
  // Carte 2+ : régulièrement dans le pool Standard (~1/5).
  return withTp[index % withTp.length]!;
}

function makeBonus(
  quality: RewardQuality,
  index: number,
  rng: Rng,
  mapId: MapId,
): RewardBonusEffect {
  if (quality === 'small') {
    const fx = RUN_BONUS_EFFECTS.small;
    const kinds = [
      'pulseSpeed',
      'bulbCapacity',
      'extraRelays',
      'extraSwitches',
      'edgeThroughput',
    ] as const;
    const kind = kinds[index % kinds.length]!;
    const copy = copyFor(kind, quality);
    return {
      kind,
      label: copy.label,
      description: copy.description,
      labelKey: `bonus.${kind}`,
      apply(mods) {
        if (kind === 'pulseSpeed') {
          mods.pulseSpeedMult += fx.pulseSpeed;
          mods.generatorProductionMult += fx.generatorProduction;
        }
        if (kind === 'bulbCapacity') mods.bulbCapacityMult += fx.bulbCapacity;
        if (kind === 'edgeThroughput') mods.edgeThroughputBonus += fx.edgeThroughput;
      },
    };
  }
  if (quality === 'standard') {
    const fx = RUN_BONUS_EFFECTS.standard;
    const kind = pickStandardKind(rng, index, mapId);
    const copy = copyFor(kind, quality);
    return {
      kind,
      label: copy.label,
      description: copy.description,
      labelKey: `bonus.${kind}`,
      apply(mods) {
        if (kind === 'pulseSpeed') {
          mods.pulseSpeedMult += fx.pulseSpeed;
          mods.generatorProductionMult += fx.generatorProduction;
        }
        if (kind === 'globalThroughput') mods.globalThroughputMult += fx.globalThroughput;
        if (kind === 'interferenceResist') mods.interferenceResist += fx.interferenceResist;
        // teleporter / extraRelays / extraSwitches via side bags in applyRewardOffer
      },
    };
  }
  const fx = RUN_BONUS_EFFECTS.large;
  const kinds = ['globalThroughput', 'interferenceResist', 'expressLine', 'digitalBridge'] as const;
  const kind = kinds[index % kinds.length]!;
  const copy = copyFor(kind, quality);
  return {
    kind,
    label: copy.label,
    description: copy.description,
    labelKey: `bonus.${kind}`,
    apply(mods) {
      if (kind === 'globalThroughput') mods.globalThroughputMult += fx.globalThroughput;
      if (kind === 'interferenceResist') mods.interferenceResist += fx.interferenceResist;
      if (kind === 'expressLine') mods.expressLine = true;
      if (kind === 'digitalBridge') mods.digitalBridge = true;
    },
  };
}

function rollQuality(rng: Rng): RewardQuality {
  const r = rng.next();
  if (r < REWARD_QUALITY_PROBABILITIES.small) return 'small';
  if (r < REWARD_QUALITY_PROBABILITIES.small + REWARD_QUALITY_PROBABILITIES.standard) {
    return 'standard';
  }
  return 'large';
}

function uniqueBonuses(
  rng: Rng,
  count: number,
  mapId: MapId,
): { quality: RewardQuality; bonus: RewardBonusEffect }[] {
  const out: { quality: RewardQuality; bonus: RewardBonusEffect }[] = [];
  const usedKinds = new Set<string>();

  for (let i = 0; i < count; i++) {
    let attempts = 0;
    while (attempts < 24) {
      const quality = rollQuality(rng);
      const bonus = makeBonus(quality, i + attempts, rng, mapId);
      if (!usedKinds.has(bonus.kind)) {
        usedKinds.add(bonus.kind);
        out.push({ quality, bonus });
        break;
      }
      attempts++;
    }
  }
  return out;
}

export function generateWeek1Reward(): RewardOffer[] {
  // Week 1: guaranteed cables + speed (no teleporter).
  const copy = copyFor('pulseSpeed', 'standard');
  return [
    {
      id: 'week1-guaranteed',
      quality: 'standard',
      cables: WEEK1_GUARANTEED_CABLES,
      bonus: {
        kind: 'pulseSpeed',
        label: copy.label,
        description: copy.description,
        labelKey: 'bonus.pulseSpeed',
        apply(mods) {
          mods.pulseSpeedMult += RUN_BONUS_EFFECTS.standard.pulseSpeed;
          mods.generatorProductionMult += RUN_BONUS_EFFECTS.standard.generatorProduction;
        },
      },
    },
  ];
}

function makeTeleporterBonus(): RewardBonusEffect {
  const copy = copyFor('teleporter', 'standard');
  return {
    kind: 'teleporter',
    label: copy.label,
    description: copy.description,
    labelKey: 'bonus.teleporter',
    apply() {
      /* inventory granted in applyRewardOffer */
    },
  };
}

export function generateWeeklyRewards(
  rng: Rng,
  week: number,
  mapId: MapId = 'core',
): RewardOffer[] {
  if (week === 1) return generateWeek1Reward();

  const rolled = uniqueBonuses(rng, 3, mapId);
  const offers: RewardOffer[] = rolled.map((entry, i) => ({
    id: `w${week}-o${i}`,
    quality: entry.quality,
    cables: REWARD_CABLES[entry.quality],
    bonus: entry.bonus,
  }));

  // Carte 2+ : souvent imposer un téléporteur parmi les 3 choix s'il manque.
  if (mapId !== 'core' && !offers.some((o) => o.bonus.kind === 'teleporter')) {
    if (rng.next() < 0.55) {
      const stdIdx = offers.findIndex((o) => o.quality === 'standard');
      const idx = stdIdx >= 0 ? stdIdx : 1;
      offers[idx] = {
        id: offers[idx]!.id,
        quality: 'standard',
        cables: REWARD_CABLES.standard,
        bonus: makeTeleporterBonus(),
      };
    }
  }

  return offers;
}

export function applyRewardOffer(
  mods: RunModifiers,
  offer: RewardOffer,
  relays: { value: number },
  teleporters: { value: number } = { value: 0 },
  switches: { value: number } = { value: 0 },
): RunModifiers {
  const next = cloneMods(mods);
  offer.bonus.apply(next);
  if (offer.bonus.kind === 'extraRelays') {
    const tier = offer.quality;
    const extra =
      tier === 'small' ? RUN_BONUS_EFFECTS.small.extraRelays : RUN_BONUS_EFFECTS.standard.extraRelays;
    relays.value += extra;
  }
  if (offer.bonus.kind === 'extraSwitches' || offer.bonus.kind === 'switch') {
    if (offer.bonus.kind === 'switch') {
      switches.value += 1;
    } else {
      const tier = offer.quality;
      const extra =
        tier === 'small'
          ? RUN_BONUS_EFFECTS.small.extraSwitches
          : RUN_BONUS_EFFECTS.standard.extraSwitches;
      switches.value += extra;
    }
  }
  if (offer.bonus.kind === 'teleporter') {
    teleporters.value += 1;
  }
  next.pulseSpeedMult = Math.min(next.pulseSpeedMult, 2.5);
  next.globalThroughputMult = Math.min(next.globalThroughputMult, 3);
  next.interferenceResist = Math.min(next.interferenceResist, 0.9);
  next.generatorProductionMult = Math.min(next.generatorProductionMult ?? 0, 1);
  return next;
}

export function rewardBonusTitle(offer: RewardOffer): string {
  return offer.bonus.label || offer.bonus.labelKey.replace('bonus.', '');
}

export function rewardBonusDescription(offer: RewardOffer): string {
  return offer.bonus.description || '';
}
